// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import { infoProps } from "@store/Themes/common/props/info";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = WidgetProps;

export interface CheckoutReviewAndSubmitERPInformationsStyles {
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    orderNumberContainer?: InjectableCss;
    orderNumberTypography?: TypographyPresentationProps;
}

export const checkoutReviewAndSubmitERPInformationsStyles: CheckoutReviewAndSubmitERPInformationsStyles = {
    headingAndTextStyles: {
        wrapper: {
            css: css`
                ${spacingStyles()}
                margin-bottom: ${pxToRem(20)};
            `,
        },
        heading: infoProps.label,
        text: infoProps.value,
    },
    infoGridContainer: infoProps.grid?.container,
    infoGridItem: infoProps.grid?.item,
    orderNumberContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            width: fit-content;
            margin-bottom: ${pxToRem(10)};
        `,
    },
    orderNumberTypography: {
        size: 20,
        weight: "bold",
    },
};

const styles = checkoutReviewAndSubmitERPInformationsStyles;

const CheckoutReviewAndSubmitERPInformations: FC<Props> = ({ fields }) => {
    const defaultText = translate("--");
    const isOrderConfirmationPage = useAppSelector(
        state => getCurrentPage(state).type === constants.pageType.orderConfirmation,
    );
    const fulfillmentMethod = useAppSelector(state => state.context.session.fulfillmentMethod);
    const orderedCart = useAppSelector(state => getCartState(state, state.pages.orderConfirmation.cartId)).value;
    const currentCart = useAppSelector(state => {
        const { cartId } = state.pages.checkoutReviewAndSubmit;
        return cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    });
    const language = useAppSelector(state => state.context.session.language);

    const cart = fields.displayCurrentCart ? currentCart : orderedCart;

    if (!cart) {
        return null;
    }

    return (
        <>
            {isOrderConfirmationPage && (
                <StyledWrapper {...styles.orderNumberContainer}>
                    <Typography as="h2" {...styles.orderNumberTypography}>
                        {`${translate("{0}:", translate("Greif Order Number"))} `}
                    </Typography>

                    <Typography as="h2" {...styles.orderNumberTypography}>
                        &nbsp;{cart.erpOrderNumber || cart.orderNumber}
                    </Typography>
                </StyledWrapper>
            )}
            <GridContainer {...styles.infoGridContainer}>
                {isOrderConfirmationPage && (
                    <>
                        {cart.orderDate && (
                            <GridItem {...styles.infoGridItem}>
                                <SmallHeadingAndText
                                    extendedStyles={styles.headingAndTextStyles}
                                    heading={translate("Order Date")}
                                    text={getLocalizedDateTime({
                                        dateTime: new Date(cart.orderDate as Date),
                                        language,
                                    })}
                                />
                            </GridItem>
                        )}
                        {cart.poNumber && (
                            <GridItem {...styles.infoGridItem}>
                                <SmallHeadingAndText
                                    extendedStyles={styles.headingAndTextStyles}
                                    heading={`${translate("PO Number")}`}
                                    text={cart.poNumber}
                                />
                            </GridItem>
                        )}
                        {cart.statusDisplay && (
                            <GridItem {...styles.infoGridItem}>
                                <SmallHeadingAndText
                                    extendedStyles={styles.headingAndTextStyles}
                                    heading={translate("Order Status")}
                                    text={cart.statusDisplay}
                                />
                            </GridItem>
                        )}
                    </>
                )}
                {fulfillmentMethod === FulfillmentMethod.Ship && (
                    <GridItem {...styles.infoGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Requested Delivery Date")}
                            text={
                                getLocalizedDateTime({
                                    dateTime: new Date(cart.requestedDeliveryDateDisplay as Date),
                                    language,
                                }) || defaultText
                            }
                        />
                    </GridItem>
                )}
                {fulfillmentMethod === FulfillmentMethod.PickUp && (
                    <GridItem {...styles.infoGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Requested Pickup Date")}
                            text={
                                getLocalizedDateTime({
                                    dateTime: new Date(cart.requestedPickupDateDisplay as Date),
                                    language,
                                }) || defaultText
                            }
                        />
                    </GridItem>
                )}
                <GridItem {...styles.infoGridItem}>
                    <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={translate("Carrier")}
                        text={cart.properties.cart_FullCarrier || defaultText}
                    />
                </GridItem>
                <GridItem {...styles.infoGridItem}>
                    <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={translate("Delivery Term")}
                        text={cart.properties.cart_FullDeliveryTerm || defaultText}
                    />
                </GridItem>
                <GridItem {...styles.infoGridItem}>
                    <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={translate("Payment Terms")}
                        text={cart.properties.cart_FullPaymentTerm || defaultText}
                    />
                </GridItem>
                {/* Store Custom Code - Removed order notes */}
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: CheckoutReviewAndSubmitERPInformations,
    definition: {
        group: "Checkout - Review & Submit",
        displayName: "ERP Informations",
        allowedContexts: ["CheckoutReviewAndSubmitPage", "OrderConfirmationPage"],
        fieldDefinitions: [
            {
                name: "displayCurrentCart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
